import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { EQUIPMENT_MANAGE_ALL, ISSUE_STATUS_META, SEVERITIES, SEVERITY_META, formatDay } from "../lib/equipment";
import CategoriesDialog from "../components/equipment/CategoriesDialog";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import IssueDetailSheet, { IssueStatusBadge, SeverityBadge } from "../components/equipment/IssueDetailSheet";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertTriangle, Download, Loader2, Printer } from "lucide-react";
import { toast } from "sonner";

const STATUS_CHOICES = [["active", "À traiter"], ["all", "Toutes"], ...Object.entries(ISSUE_STATUS_META).map(([k, m]) => [k, m.label])];

// « Matériel des pharmacies »: the equipment issues to follow. Everyone sees the issues they are responsible for and those of the
// missions they see; equipment:view_all sees every pharmacy's and may export. A row opens the issue's drawer (?anomalie=<id>).
export default function Equipment() {
  const [params, setParams] = useSearchParams();
  const status = params.get("statut") || "active";
  const severity = params.get("gravite") || "all";
  const mine = params.get("responsable") === "moi";
  const overdue = params.get("retard") === "1";
  const issueId = params.get("anomalie");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const { user } = useAuth();

  const query = useCallback(() => {
    const q = {};
    if (status !== "all") q.status = status;
    if (severity !== "all") q.severity = severity;
    if (mine) q.responsible = "me";
    if (overdue) q.overdue = true;
    return q;
  }, [status, severity, mine, overdue]);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/equipment-issues", { params: query() });
      setData(d);
      setError("");
    } catch (e) {
      setError(errorMessage(e, "Impossible de charger les anomalies."));
    }
  }, [query]);
  useEffect(() => { load(); }, [load]);

  const setParam = (key, value, fallback) => {
    const next = new URLSearchParams(params);
    if (value === fallback || value === "" || value === false) next.delete(key); else next.set(key, value === true ? "1" : value);
    setParams(next, { replace: true });
  };
  const openIssue = (id) => setParam("anomalie", id || "", "");

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data: blob } = await api.get("/equipment-issues/export.csv", { params: query(), responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "anomalies-materiel.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch (e) {
      toast.error("Export impossible : réduisez les filtres ou réessayez.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="equipment-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-2xl text-slate-900">Matériel des pharmacies</h1>
          <p className="text-sm text-slate-500">Anomalies relevées sur le matériel, suivies jusqu'à leur résolution.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {hasPermission(user, EQUIPMENT_MANAGE_ALL) && (
            <Button variant="outline" onClick={() => setCategoriesOpen(true)} data-testid="equipment-categories">Catégories</Button>
          )}
          {data?.capabilities?.export && (
            <Button variant="outline" onClick={exportCsv} disabled={exporting} data-testid="equipment-export">
              {exporting ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Download size={15} className="mr-1.5" />}Exporter (CSV)
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="equipment-filters">
        <Select value={status} onValueChange={(v) => setParam("statut", v, "active")}>
          <SelectTrigger className="w-40" aria-label="Statut" data-testid="equipment-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUS_CHOICES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={severity} onValueChange={(v) => setParam("gravite", v, "all")}>
          <SelectTrigger className="w-40" aria-label="Gravité" data-testid="equipment-filter-severity"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes gravités</SelectItem>
            {SEVERITIES.map((s) => <SelectItem key={s} value={s}>{SEVERITY_META[s].label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant={mine ? "default" : "outline"} size="sm" className="h-10" aria-pressed={mine} onClick={() => setParam("responsable", mine ? "" : "moi", "")}
          data-testid="equipment-filter-mine">Mes anomalies</Button>
        <Button variant={overdue ? "default" : "outline"} size="sm" className="h-10" aria-pressed={overdue} onClick={() => setParam("retard", !overdue, false)}
          data-testid="equipment-filter-overdue">En retard</Button>
      </div>

      {error && !data ? (
        <Card className="p-5 text-sm text-slate-600 flex items-center gap-2 flex-wrap">{error}<Button variant="outline" size="sm" onClick={load}>Réessayer</Button></Card>
      ) : !data ? (
        <Card className="p-5 space-y-2"><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-full" /></Card>
      ) : data.items.length === 0 ? (
        <Card className="p-8 text-center" data-testid="equipment-empty">
          <Printer size={22} className="mx-auto text-slate-300" />
          <p className="mt-2 text-sm font-medium text-slate-900">Aucune anomalie {status === "active" ? "à traiter" : "pour ces filtres"}</p>
          <p className="text-sm text-slate-500">Les anomalies sont signalées pendant les missions, depuis la carte « Matériel ».</p>
        </Card>
      ) : (
        <>
          <Card className="hidden md:block overflow-hidden">
            <table className="w-full text-sm" data-testid="equipment-table">
              <thead className="bg-slate-50 text-xs font-semibold text-slate-600">
                <tr>
                  <th className="px-4 py-2.5 text-left">Anomalie</th><th className="px-4 py-2.5 text-left">Pharmacie</th>
                  <th className="px-4 py-2.5 text-left">Gravité</th><th className="px-4 py-2.5 text-left">Statut</th>
                  <th className="px-4 py-2.5 text-left">Responsable</th><th className="px-4 py-2.5 text-left">Échéance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.map((i) => (
                  <tr key={i.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openIssue(i.id)} data-testid={`equipment-row-${i.id}`}>
                    <td className="px-4 py-3">
                      <button type="button" className="text-left font-medium text-slate-900 hover:underline" onClick={(e) => { e.stopPropagation(); openIssue(i.id); }}>{i.title}</button>
                      <div className="text-xs text-slate-500">{i.number}{i.asset_label ? ` · ${i.asset_label}` : ""}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{i.pharmacy_name}</td>
                    <td className="px-4 py-3"><SeverityBadge severity={i.severity} /></td>
                    <td className="px-4 py-3"><IssueStatusBadge status={i.status} /></td>
                    <td className="px-4 py-3 text-slate-700">{i.responsible_name}</td>
                    <td className={`px-4 py-3 ${i.overdue ? "text-red-700 font-medium" : "text-slate-700"}`}>{i.due_date ? formatDay(i.due_date) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <ul className="md:hidden space-y-2" data-testid="equipment-cards">
            {data.items.map((i) => (
              <li key={i.id}>
                <button type="button" onClick={() => openIssue(i.id)} className="w-full rounded-xl border border-slate-200 bg-white p-3 text-left space-y-1.5" data-testid={`equipment-card-${i.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-medium text-slate-900 break-words">{i.title}</span>
                    <SeverityBadge severity={i.severity} />
                  </div>
                  <div className="text-xs text-slate-500">{i.pharmacy_name} · {i.number}{i.asset_label ? ` · ${i.asset_label}` : ""}</div>
                  <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-600">
                    <IssueStatusBadge status={i.status} /><span>{i.responsible_name}</span>
                    {i.due_date && <span className={i.overdue ? "text-red-700 font-medium inline-flex items-center gap-1" : ""}>{i.overdue && <AlertTriangle size={11} />}{formatDay(i.due_date)}</span>}
                  </div>
                </button>
              </li>
            ))}
          </ul>
          {data.truncated && <p className="text-xs text-slate-500">Seules les 300 plus récentes sont affichées : affinez les filtres.</p>}
        </>
      )}

      <CategoriesDialog open={categoriesOpen} onOpenChange={setCategoriesOpen} />
      <IssueDetailSheet issueId={issueId} onOpenChange={(open) => { if (!open) openIssue(null); }} onChanged={load} />
    </div>
  );
}
